import { Column, Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';
import { LoanStatusRow } from 'app/components/loan-status-row';
import { PCImage } from 'app/design/primitives/image';
import React from 'react';
import type { Loan } from 'app/types/loan';
import type { User } from 'app/types/user';
import { useTranslation } from 'react-i18next';

import { localeAwareFormat } from 'app/lib/date';
import { LoanStatus, LoanType } from 'app/types/loan';
import { ListingSubtype, TransactionMode } from 'app/types/listing';
import { isDonationListing } from 'app/lib/listing';
import { Separator } from 'app/design/primitives/text-divider';
import { View } from 'react-native';
import { getLoanInfos } from '../functions';
import { createTheme as createDSTheme } from 'app/shopify';
import { ListingThumbnail } from 'app/components/listing-thumbnail';
import type { TFunction } from 'i18next';
const theme = createDSTheme();

const ucFirst = (str: string) => (!str ? undefined : str.charAt(0).toUpperCase() + str.slice(1));

function getOtherUserRole(isSell: boolean, isDonation: boolean, loanType?: LoanType) {
  if (isSell) {
    return loanType === LoanType.Lending ? 'buyer' : 'seller';
  }
  if (isDonation) {
    return loanType === LoanType.Lending ? 'recipient' : 'donor';
  }
  return loanType === LoanType.Lending ? 'borrower' : 'lender';
}

function getLocalizedDates(loan: Loan | null | undefined, t: TFunction<'one-loan'>) {
  if (!loan) {
    return { localizedEnd: '-', localizedStart: '-' };
  }

  const flexibleDatesLabel = loan.flexibleDates ? ` ${t('flexibleDates', { ns: 'loanDatesPicker' })}` : '';
  const localizedStart =
    localeAwareFormat(new Date(loan.start), t('dateFormat_MMMMd', { ns: 'common' })) +
    (loan.listing.timeStart ? `, ${loan.listing.timeStart}` : '');
  const localizedEnd =
    (loan.listing.timeEnd && loan.start === loan.end
      ? loan.listing.timeEnd
      : localeAwareFormat(new Date(loan.end), t('dateFormat_MMMMd', { ns: 'common' })) +
        (loan.listing.timeEnd && loan.start !== loan.end ? `, ${loan.listing.timeEnd}` : '')) + flexibleDatesLabel;

  return { localizedEnd, localizedStart };
}

function Counterpart({ otherUser, role, t }: { otherUser?: Loan['borrower']; role: string; t: TFunction<'one-loan'> }) {
  if (!otherUser) {
    return null;
  }

  const profile = otherUser.profile;
  const nameParts: string[] = [];
  if (profile?.firstName) nameParts.push(profile.firstName);
  if (profile?.lastName) nameParts.push(profile.lastName);
  const joinedName = nameParts.join(' ');
  const otherUserName = joinedName.length > 0 ? joinedName : t('deletedUser', { ns: 'common' });

  return (
    <Row alignItems="center" gap="spacing.general.x2">
      {profile?.picture ? (
        <PCImage
          aspectRatio={1}
          height={32}
          width={32}
          source={{ uri: profile.picture }}
          style={{ borderRadius: 16 }}
        />
      ) : null}
      <Column flex={1} gap="spacing.general.x0,5">
        <Text color="text.secondary" fontSize={12} numberOfLines={1}>
          {t(`counterpart.${role}`)}
        </Text>
        <Text fontWeight="700" numberOfLines={1} variant="bodyBase">
          {otherUserName}
        </Text>
      </Column>
    </Row>
  );
}

function LoanPeriod({
  isDonation,
  isSell,
  isService,
  loan,
  localizedEnd,
  localizedStart,
  t,
}: {
  isDonation: boolean;
  isSell: boolean;
  isService: boolean;
  loan: Loan;
  localizedEnd: string;
  localizedStart: string;
  t: TFunction<'one-loan'>;
}) {
  if (isSell || isDonation) {
    return (
      <Column flex={1} gap="spacing.general.x2">
        <Text fontWeight="700" variant="bodyBase">
          {t(isDonation ? 'donationDate' : 'transactionDate')}
        </Text>
        <Text color="text.secondary" variant="bodyBase">
          {ucFirst(localizedStart)}
        </Text>
      </Column>
    );
  }

  return (
    <>
      <Column flex={1} gap="spacing.general.x2">
        <Text fontWeight="700" variant="bodyBase">
          {t('start')}
        </Text>
        <Text color="text.secondary" variant="bodyBase">
          {ucFirst(localizedStart)}
        </Text>
      </Column>

      <View
        style={{
          backgroundColor: theme.colors['surface.default'],
          marginHorizontal: theme.spacing['spacing.general.x4'],
          width: 1,
        }}
      />

      <Column flex={1} gap="spacing.general.x2">
        <Text fontWeight="700" variant="bodyBase">
          {isService ? t('endService') : t(loan.status === LoanStatus.Completed ? 'expectedEnd' : 'end')}
        </Text>
        <Text color="text.secondary" variant="bodyBase">
          {ucFirst(localizedEnd)}
        </Text>
      </Column>
    </>
  );
}

export function Head({ loan, user }: { loan?: Loan | null; user?: User }) {
  const { t } = useTranslation('one-loan');
  const isService = loan?.listing.subtype === ListingSubtype.service;
  const isDonation = isDonationListing(loan?.listing);
  const isSell = loan?.listing.transactionMode === TransactionMode.sell;
  const { localizedEnd, localizedStart } = getLocalizedDates(loan, t);
  const loanInfos = user ? getLoanInfos(loan, user) : undefined;
  const otherUser = loanInfos?.otherUser;
  const otherUserRole = getOtherUserRole(isSell, isDonation, loanInfos?.loanType);

  if (!loan) {
    return null;
  }

  return (
    <Column
      gap="spacing.general.x4"
      padding="spacing.general.x4"
      backgroundColor="surface.tertiary-neutral"
      borderRadius="radius.lg"
    >
      <Row alignItems="center" gap="spacing.general.x3">
        {loan.listing.picture ? <ListingThumbnail {...loan.listing} height={100} width={100} /> : null}
        <Column flex={1} gap="spacing.general.x2">
          <Text fontWeight="700" numberOfLines={1} variant="bodyBase">
            {loan.listing.item.name}
          </Text>
          <Counterpart otherUser={otherUser} role={otherUserRole} t={t} />
        </Column>
      </Row>

      <Separator onlyWidth color={theme.colors['surface.default']} />

      <Column gap="spacing.general.x2">
        <Text fontSize={16} fontWeight="bold" variant="bodyBase">
          {t('status')}
        </Text>
        <LoanStatusRow
          end={loan.end}
          isDonation={isDonation}
          listingSubtype={loan.listing.subtype}
          returned={loan.returned}
          showClock={false}
          start={loan.start}
          status={loan.status}
          transactionMode={loan.listing.transactionMode}
        />
      </Column>

      <Separator onlyWidth color={theme.colors['surface.default']} />

      <Row>
        <LoanPeriod
          isDonation={isDonation}
          isSell={isSell}
          isService={isService}
          loan={loan}
          localizedEnd={localizedEnd}
          localizedStart={localizedStart}
          t={t}
        />
      </Row>
    </Column>
  );
}
